import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { cx } from './format';
import { Empty } from './Feedback';
import s from './ui.module.css';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Makes the column sortable by this value. */
  sort?: (row: T) => number | string;
  align?: 'right';
  width?: number | string;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

interface Props<T> {
  rows: readonly T[];
  columns: Array<Column<T>>;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  initialSort?: SortState;
  empty?: ReactNode;
  /** What to do about an empty list (shown under `empty`). */
  emptyHint?: ReactNode;
  caption?: string;
  /** Rows per page; pages appear only when there are more rows than this. */
  pageSize?: number;
  /** Scrolls inside this height with the header kept in view. */
  maxHeight?: number;
}

/** Dense table: sticky ink header, dividers and zebra rows, optional sorting and paging. */
export function DataTable<T>({ rows, columns, rowKey, onRowClick, selectedKey, initialSort, empty = '沒有資料。', emptyHint, caption, pageSize, maxHeight }: Props<T>) {
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!sort || !col?.sort) return rows;
    const get = col.sort;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = get(a);
      const y = get(b);
      return sign * (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y)));
    });
  }, [rows, columns, sort]);

  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  useEffect(() => {
    if (page >= pages) setPage(0);
  }, [page, pages]);

  if (rows.length === 0) return <Empty hint={emptyHint}>{empty}</Empty>;

  const toggle = (key: string) => setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  const shown = pageSize ? sorted.slice(page * pageSize, (page + 1) * pageSize) : sorted;

  return (
    <>
      <div className={s.tableWrap} style={maxHeight ? { maxHeight } : undefined}>
        <table className={s.table}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    className={cx(c.align === 'right' && s.alignRight)}
                    style={c.width !== undefined ? { width: c.width } : undefined}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.sort ? (
                      <button type="button" className={s.sortBtn} onClick={() => toggle(c.key)}>
                        {c.header}
                        <span className={s.sortIcon} aria-hidden>
                          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => {
              const k = rowKey(row);
              return (
                <tr
                  key={k}
                  className={cx(onRowClick && s.clickable, selectedKey === k && s.selected)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onRowClick(row)) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cx(c.align === 'right' && s.alignRight)}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && <Pager page={page} pages={pages} total={sorted.length} pageSize={pageSize!} onPage={setPage} />}
    </>
  );
}

function Pager({ page, pages, total, pageSize, onPage }: { page: number; pages: number; total: number; pageSize: number; onPage: (p: number) => void }) {
  // First, last, and two either side of the current page.
  const nums = [...new Set([0, pages - 1, page - 1, page, page + 1].filter((p) => p >= 0 && p < pages))].sort((a, b) => a - b);
  return (
    <nav className={s.pager} aria-label="分頁">
      <span className={s.pagerInfo}>
        第 {page * pageSize + 1}–{Math.min(total, (page + 1) * pageSize)} 項，共 {total} 項
      </span>
      <button type="button" disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="上一頁">
        ‹
      </button>
      {nums.map((p, i) => (
        <span key={p} style={{ display: 'contents' }}>
          {i > 0 && p - nums[i - 1]! > 1 && <span>…</span>}
          <button type="button" aria-current={p === page ? 'page' : undefined} onClick={() => onPage(p)}>
            {p + 1}
          </button>
        </span>
      ))}
      <button type="button" disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="下一頁">
        ›
      </button>
    </nav>
  );
}
